import { getApiBaseUrl } from "../utils/ssoProvider";
import { useEffect, useState } from "react";
import { captureException } from "@sentry/react";
import axiosInstance from "./axiosInstance";

export const formatDate = dateString => {
  const date = new Date(dateString);
  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = date.getUTCFullYear();
  const formattedDate = `${day}/${month}/${year}`;
  return formattedDate;
};

export const formatTime = dateString => {
  const date = new Date(dateString);
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    // `hourCycle: "h23"`, NOT `hour12: false`. The two agree on every hour except
    // midnight, where `hour12: false` picks the h24 cycle on older ICU (Node 20,
    // which CI runs) and renders 00:00 as "24:00"; Node 22 returns "00:00". That
    // made the same code show a different time to users depending on the runtime.
    // h23 is 00–23 on both, so midnight is always "00:00".
    hourCycle: "h23",
    timeZone: "UTC",
  });
  return formattedTime;
};

export const formatDay = dateString => {
  const date = new Date(dateString);
  const formattedDay = date.toLocaleString("en-US", {
    weekday: "short",
    timeZone: "UTC",
  });
  return formattedDay;
};

export const formatCustomDateTime = (dateString, fullYear = true) => {
  const date = new Date(dateString);

  const day = date.getUTCDate();
  const month = date.toLocaleString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  const year = fullYear ? date.getUTCFullYear() : date.getUTCFullYear().toString().slice(-2); // Conditionally format year
  const time = date
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZone: "UTC",
    })
    ?.toLowerCase();

  if (fullYear) {
    return `${month} ${day}, ${year}`;
  } else {
    return `${day} ${month} '${year}, ${time}`;
  }
};

export const formatCustomISO = date => {
  // Get the components of the date
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed, so add 1
  const day = String(date.getDate()).padStart(2, "0");
  const hours = "00";
  const minutes = "00";
  const seconds = "00";
  const milliseconds = "000";
  const microseconds = "850";

  // Combine into the desired format
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${microseconds}`;
};

// Web storage is not always available: Firefox's strict mode and "block site
// data" settings make even READING localStorage throw a SecurityError, and a
// full quota makes setItem throw QuotaExceededError. Both helpers are called all
// over the app - setInBothStorages runs ~56 times across the login path alone -
// so an unguarded throw here aborts a sign-in midway and leaves auth half
// written. Degrade instead: a read returns null (treated as "not logged in",
// which routes to /signin) and a write is a no-op, so the app stays usable and
// recoverable rather than dying on a blank screen.
const safeStorage = storage => {
  try {
    // Touching the property itself can throw, so this is inside the try.
    return typeof window !== "undefined" && window[storage] ? window[storage] : null;
  } catch (_) {
    return null;
  }
};

export const getStoredValue = key => {
  try {
    const session = safeStorage("sessionStorage")?.getItem(key);
    if (session) return session;
    return safeStorage("localStorage")?.getItem(key) ?? null;
  } catch (_) {
    return null;
  }
};

export const setInBothStorages = (key, value) => {
  // Each storage is written independently: if localStorage is full but
  // sessionStorage works, the session still gets its token.
  ["localStorage", "sessionStorage"].forEach(name => {
    try {
      safeStorage(name)?.setItem(key, value);
    } catch (_) {
      // Storage unavailable or full - the app must keep working without it.
    }
  });
};

// setInBothStorages's read-side counterpart for removeItem/clear: swallow a
// throw from either storage instead of letting it abort the caller (which
// otherwise happens after the state it's meant to clean up already changed).
export const removeFromBothStorages = key => {
  ["localStorage", "sessionStorage"].forEach(name => {
    try {
      safeStorage(name)?.removeItem(key);
    } catch (_) {
      // Storage unavailable - nothing to clean up.
    }
  });
};

export const safeSessionStorage = {
  getItem: key => {
    try {
      return safeStorage("sessionStorage")?.getItem(key) ?? null;
    } catch (_) {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      safeStorage("sessionStorage")?.setItem(key, value);
    } catch (_) {
      // Storage unavailable or full - the app must keep working without it.
    }
  },
  removeItem: key => {
    try {
      safeStorage("sessionStorage")?.removeItem(key);
    } catch (_) {
      // Storage unavailable - nothing to clean up.
    }
  },
  clear: () => {
    try {
      safeStorage("sessionStorage")?.clear();
    } catch (_) {
      // Storage unavailable - nothing to clean up.
    }
  },
};

export const safeLocalStorage = {
  getItem: key => {
    try {
      return safeStorage("localStorage")?.getItem(key) ?? null;
    } catch (_) {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      safeStorage("localStorage")?.setItem(key, value);
    } catch (_) {
      // Storage unavailable or full - the app must keep working without it.
    }
  },
  removeItem: key => {
    try {
      safeStorage("localStorage")?.removeItem(key);
    } catch (_) {
      // Storage unavailable - nothing to clean up.
    }
  },
  clear: () => {
    try {
      safeStorage("localStorage")?.clear();
    } catch (_) {
      // Storage unavailable - nothing to clean up.
    }
  },
};

// True when neither storage can be written. The app still runs, but nothing
// persists across a reload, so callers can warn the user instead of letting
// them hit a confusing half-logged-in state later.
export const isStorageAvailable = () => {
  const probe = "__storage_probe__";
  return ["sessionStorage", "localStorage"].some(name => {
    try {
      const s = safeStorage(name);
      if (!s) return false;
      s.setItem(probe, "1");
      s.removeItem(probe);
      return true;
    } catch (_) {
      return false;
    }
  });
};

// logics.js

export const formatDateTime = dateString => {
  const date = new Date(dateString);
  const now = new Date();

  // Create Date objects for comparison without time
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const emailDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Calculate the difference in days
  const diffInTime = today.getTime() - emailDate.getTime();
  const diffInDays = diffInTime / (1000 * 3600 * 24);

  if (diffInDays === 0) {
    // Draft is from today: show time
    return formatTime(dateString);
  } else {
    // Draft is from yesterday or earlier: show date
    return formatCustomDateTime(dateString);
  }
};

export const useNetworkStatus = () => {
  const [isOnline, setOnline] = useState(true);
  const API_URL = getApiBaseUrl();

  const updateNetworkStatus = async () => {
    // navigator.onLine only reflects whether the OS reports a network
    // interface as connected, not whether the backend is actually reachable -
    // it's known to report false transiently right after a page load/
    // navigation (observed: a real user + this app's own E2E suite both hit
    // the offline screen with zero /v1/auth/check-internet request ever sent,
    // confirmed via Resource Timing entries - navigator.onLine was already
    // back to true by the time it was inspected, but had clearly been false
    // for this one synchronous check). The real request below is already the
    // actual source of truth for reachability, so don't let this flaky signal
    // skip it and short-circuit straight to "offline".
    try {
      const response = await axiosInstance.get(`${API_URL}/v1/auth/check-internet`);
      setOnline(response?.status === 200);
    } catch (error) {
      // This replaces the ENTIRE app with a full-page "No internet connection"
      // screen, so whatever actually failed here needs to be visible - there
      // was zero telemetry on this path before, which is why the
      // navigator.onLine bug above went unnoticed for so long. Capture the
      // real error (network timeout, CORS, a genuine 5xx, an aborted request
      // from an in-flight page navigation, etc.) rather than only ever
      // knowing "the user saw the offline screen".
      try {
        captureException(error, { tags: { feature: "check-internet" } });
      } catch (_) {
        // monitoring unavailable - still show the offline screen below
      }
      setOnline(false);
    }
  };

  return { isOnline, updateNetworkStatus };
};

export const preserveExactSpacing = markdown => {
  if (typeof markdown !== "string") return "";
  // Convert literal \n strings to actual newlines first
  const normalizedMarkdown = markdown.replace(/\\n/g, "\n");
  return normalizedMarkdown.replace(/\n{2,}/g, match => {
    if (match.length < 2) return match;
    const preserved = "\n\n";
    const remaining = match.length - 2;
    const brCount = Math.floor(remaining / 2);
    return preserved + "<br />\n".repeat(brCount);
  });
};

// Matches a leftover angle-bracket placeholder such as `<customer_name>` or
// `< your_name >` - a SINGLE snake/word token (no internal spaces) inside angle
// brackets. Deliberately narrow so it never touches legitimate prose like
// `a < b and c > d` or an email address `<jane@x.com>`. Mirrors
// TemplateAgent._sanitize_placeholders in backend/app/emails/agent.py.
const ANGLE_PLACEHOLDER_RE = /<\s*([A-Za-z][A-Za-z0-9_]*)\s*>/g;

// MDXEditor parses markdown as MDX, so a raw `<customer_name>` token in template
// content (some backend flows, e.g. the LLM-driven "save as template" prompt, don't
// reliably escape these before saving) gets misread as an invalid JSX tag and blanks the
// whole editor instead of rendering. HTML-escaping any surviving `<placeholder>` as
// `&lt;placeholder&gt;` - exactly the form the Variables dropdown already writes when
// inserting a brand-new token - keeps MDXEditor safe, in both the read-only detail view
// and the editable create/edit form.
//
// Deliberately escaping rather than converting to square brackets: this function's return
// value is also what the create/edit page saves back on Update/Create (the editor's live
// state - sanitized or not - is what gets sent to the backend), so whichever form this
// produces becomes the template's new on-disk value even when the user didn't touch the
// placeholder at all. `&lt;.../&gt;` is already the backend's fully-supported placeholder
// convention (both the template filler and the fill-comparison node unescape exactly this
// form), so a no-op save leaves the template in a shape every part of the system already
// understands, instead of silently introducing an unsupported square-bracket form.
export const sanitizeAnglePlaceholders = content => {
  if (!content) return content;
  return content.replace(ANGLE_PLACEHOLDER_RE, "&lt;$1&gt;");
};
